import { devLog, devError } from '../utils/devLogger';
import React, { useEffect, useMemo, useState } from 'react';
import { Card, Table, Alert, Spinner, Badge } from 'react-bootstrap';
import { collection, getDocs, orderBy, query, where } from 'firebase/firestore';
import { db } from '../firebase';

const EVENT_ID = 'faith_session_2026_01_21';

const allowedCohortNames = [
  'Takealot Personal Shopper - NPI Cohort 1',
  'Takealot Personal Shopper - NPI Cohort 2',
  'Takealot Personal Shopper - NPI Cohort 3',
  'Takealot Personal Shopper - NPI Cohort 4'
];

const sessions = [
  {
    id: 'morning',
    name: "Faith's Session (Morning)",
    time: 'Wednesday, 21 Jan 2026, 8:00 AM - 10:30 AM',
  },
  {
    id: 'evening',
    name: "Faith's Session (Evening)",
    time: 'Wednesday, 21 Jan 2026, 5:00 PM - 7:30 PM',
  },
];

const formatTimestamp = (value) => {
  if (!value) return 'N/A';
  const date = value.toDate ? value.toDate() : new Date(value);
  if (Number.isNaN(date.getTime())) return 'Invalid';
  return date.toLocaleString('en-ZA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const SessionRSVPAdmin = () => {
  const [loading, setLoading] = useState(true);
  const [rsvps, setRsvps] = useState([]);
  const [eligibleLearners, setEligibleLearners] = useState([]);
  const [error, setError] = useState('');

  useEffect(() => {
    const loadRsvps = async () => {
      try {
        setLoading(true);
        setError('');

        const cohortsSnapshot = await getDocs(collection(db, 'cohorts'));
        const allowedCohorts = cohortsSnapshot.docs
          .map(doc => ({ id: doc.id, ...doc.data() }))
          .filter(cohort => {
            const name = cohort.name || cohort.cohortName || '';
            return allowedCohortNames.includes(name);
          });
        const allowedCohortIds = allowedCohorts.map(cohort => cohort.id);
        const cohortNameMap = allowedCohorts.reduce((acc, cohort) => {
          acc[cohort.id] = cohort.name || cohort.cohortName || cohort.id;
          return acc;
        }, {});

        const learners = [];
        for (let i = 0; i < allowedCohortIds.length; i += 10) {
          const chunk = allowedCohortIds.slice(i, i + 10);
          if (chunk.length === 0) continue;
          const learnersQuery = query(
            collection(db, 'users'),
            where('role', '==', 'learner'),
            where('cohortId', 'in', chunk)
          );
          const learnersSnapshot = await getDocs(learnersQuery);
          learnersSnapshot.docs.forEach(doc => {
            const data = doc.data();
            learners.push({
              id: doc.id,
              email: data.email || '',
              name: data.displayName || data.name || data.email || 'N/A',
              cohortId: data.cohortId || '',
              cohortName: cohortNameMap[data.cohortId] || data.cohortName || data.cohortId || ''
            });
          });
        }
        setEligibleLearners(learners);

        const rsvpRef = collection(db, 'events', EVENT_ID, 'rsvps');
        const rsvpQuery = query(rsvpRef, orderBy('rsvpedAt', 'desc'));
        const snapshot = await getDocs(rsvpQuery);
        const records = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setRsvps(records);
      } catch (err) {
        devError('Error loading RSVPs:', err);
        setError('Could not load RSVPs. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    loadRsvps();
  }, []);

  const sessionCounts = useMemo(() => {
    return sessions.reduce((acc, session) => {
      acc[session.id] = rsvps.filter(rsvp => rsvp.sessionId === session.id).length;
      return acc;
    }, {});
  }, [rsvps]);

  const nonRsvps = useMemo(() => {
    const rsvpIds = new Set(rsvps.map(rsvp => rsvp.studentId || rsvp.id));
    return eligibleLearners.filter(learner => !rsvpIds.has(learner.id));
  }, [eligibleLearners, rsvps]);

  return (
    <Card className="shadow-sm">
      <Card.Header className="d-flex justify-content-between align-items-center">
        <div>
          <h6 className="mb-1">Session RSVPs</h6>
          <small className="text-muted">Event ID: {EVENT_ID}</small>
        </div>
        <div className="d-flex gap-2 flex-wrap">
          {sessions.map(session => (
            <Badge key={session.id} bg="secondary">
              {session.name}: {sessionCounts[session.id] || 0}
            </Badge>
          ))}
          <Badge bg="info">
            Eligible learners: {eligibleLearners.length}
          </Badge>
          <Badge bg="warning" text="dark">
            Not RSVP&apos;d: {nonRsvps.length}
          </Badge>
        </div>
      </Card.Header>
      <Card.Body>
        {error && <Alert variant="danger">{error}</Alert>}

        {loading ? (
          <div className="text-center">
            <Spinner animation="border" variant="primary" />
            <p className="mt-2">Loading RSVPs...</p>
          </div>
        ) : (
          <>
            {rsvps.length === 0 ? (
              <Alert variant="info">
                No RSVPs submitted yet.
              </Alert>
            ) : (
              <>
                <h6>RSVPs Submitted</h6>
                <Table striped bordered hover responsive>
                  <thead>
                    <tr>
                      <th>Learner</th>
                      <th>Email</th>
                      <th>Session</th>
                      <th>Session Time</th>
                      <th>RSVP Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rsvps.map((rsvp) => (
                      <tr key={rsvp.id}>
                        <td>{rsvp.studentName || 'N/A'}</td>
                        <td>{rsvp.studentEmail || 'N/A'}</td>
                        <td>{rsvp.sessionName || 'N/A'}</td>
                        <td>{rsvp.sessionTime || 'N/A'}</td>
                        <td>{formatTimestamp(rsvp.rsvpedAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </>
            )}

            <hr />

            <h6>Not Yet RSVP&apos;d</h6>
            {nonRsvps.length === 0 ? (
              <Alert variant="success" className="mb-0">
                Everyone in the eligible cohorts has RSVP&apos;d.
              </Alert>
            ) : (
              <Table striped bordered hover responsive>
                <thead>
                  <tr>
                    <th>Learner</th>
                    <th>Email</th>
                    <th>Cohort</th>
                  </tr>
                </thead>
                <tbody>
                  {nonRsvps.map((learner) => (
                    <tr key={learner.id}>
                      <td>{learner.name}</td>
                      <td>{learner.email || 'N/A'}</td>
                      <td>{learner.cohortName || 'N/A'}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </>
        )}
      </Card.Body>
    </Card>
  );
};

export default SessionRSVPAdmin;
